import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { message } from "antd";
import {
  CalendarOutlined,
  ControlOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import { API_ROUTES } from "../../../api/ApiRoutes";
import AppContext from "../../../context/AppContext";
import { useDebouncedSearchQuery } from "../../../hooks/useDebouncedSearchQuery";
import usePaymentTransactions, {
  buildPaymentTransactionFilterParams,
} from "../../../queries/payments/usePaymentTransactions";
import usePaymentTransactionsExport from "../../../queries/payments/usePaymentTransactionsExport";
import usePaymentTransactionsExportColumnCatalog from "../../../queries/payments/usePaymentTransactionsExportColumnCatalog";
import { AsyncExportButton } from "../../shared/AsyncExport";
import ConfigurableDataTable from "../../shared/dashboards/ConfigurableDataTable";
import PaymentDetailsDrawer from "../../shared/PaymentDetailsDrawer/PaymentDetailsDrawer";
import { AGENT_ACTIONS, SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { formatDateNoTimezone,
  getAdminNow,
} from "../../../utils/dateService";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import Input from "../../shared/Input/Input";
import ReportFiltersDrawer from "./ReportFiltersDrawer/ReportFiltersDrawer";
import { buildReportGeneratorColumns } from "./reportGeneratorColumns";
import styles from "./stylesTransactionsTable.module.css";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

const MAX_RELATIVE_DATE_RANGE_DAYS = 31;

const getInitialDates = () => [
  getAdminNow().startOf("month"),
  getAdminNow().endOf("month"),
];

function getDateRangeLabel(dates) {
  const today = getAdminNow();
  const selectedDays =
    dates[1].startOf("day").diff(dates[0].startOf("day"), "day") + 1;
  const isCurrentMonth =
    dates[0].isSame(today.startOf("month"), "day") &&
    dates[1].isSame(today.endOf("month"), "day");
  const endsToday = dates[1].isSame(today, "day");

  if (isCurrentMonth) return AppStrings.PAM_REPORT_CURRENT_MONTH;
  if (selectedDays === 1 && endsToday) return AppStrings.TODAY;
  if (endsToday && selectedDays <= MAX_RELATIVE_DATE_RANGE_DAYS) {
    return formatString(AppStrings.PAM_REPORT_LAST_X_DAYS, {
      days: String(selectedDays),
    });
  }
  return formatString(AppStrings.PAM_REPORT_SELECTED_DATE_RANGE, {
    start: formatDateNoTimezone(dates[0], "MMM D, YYYY"),
    end: formatDateNoTimezone(dates[1], "MMM D, YYYY"),
  });
}

const toSortValue = (sorter) => {
  const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
  const field = activeSorter?.field ?? activeSorter?.columnKey;
  if (!["date", "amount"].includes(field) || !activeSorter?.order) {
    return "-date";
  }
  return activeSorter.order === "ascend" ? `+${field}` : `-${field}`;
};

export const buildPaymentTransactionsExportPayload = ({
  dates,
  search,
  filters,
}) => ({
  start_date: dates[0].format("YYYY-MM-DD"),
  end_date: dates[1].format("YYYY-MM-DD"),
  ...(search ? { search } : {}),
  ...buildPaymentTransactionFilterParams(filters),
});

export default function TransactionsTable() {
  usePageTitle(AppStrings.PAM_REPORT_GENERATOR);

  const { adminActions = [], setActiveSidebarTab } = useContext(AppContext);
  const [dates, setDates] = useState(getInitialDates);
  const [searchDraft, setSearchDraft] = useState("");
  const { effectiveSearchQuery: search } = useDebouncedSearchQuery(
    searchDraft,
    { delayMs: 350, minLength: 1 },
  );
  const [appliedFilters, setAppliedFilters] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [sort, setSort] = useState("-date");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);

  const queryArgs = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
      page,
      size,
      sort,
      ...(search ? { search } : {}),
      ...appliedFilters,
    }),
    [appliedFilters, dates, page, search, size, sort],
  );

  const transactionsQuery = usePaymentTransactions(queryArgs);
  const canExport = adminActions.includes(
    AGENT_ACTIONS.PAYMENTS_REPORTS_EXPORT,
  );
  const exportState = usePaymentTransactionsExport();
  const exportColumnsQuery = usePaymentTransactionsExportColumnCatalog({
    enabled: canExport,
  });
  const exportColumns = useMemo(
    () =>
      exportColumnsQuery.data.map((column) => ({
        key: column.key,
        label: column.label,
        locked: column.always_included,
      })),
    [exportColumnsQuery.data],
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setActiveSidebarTab?.(SIDER_TABS.PAYMENTS, "Transactions");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (transactionsQuery.isError) {
      message.error(AppStrings.PAM_REPORT_LOAD_ERROR);
    }
  }, [transactionsQuery.isError]);

  const openUserOverview = useCallback((record) => {
    if (!record?.player) return;
    window.open(
      `${NAVIGATION_ROUTES.USER_MANAGER}?userId=${encodeURIComponent(record.player)}`,
      "_blank",
    );
  }, []);

  const columns = useMemo(
    () => buildReportGeneratorColumns({ onOpenUser: openUserOverview, sort }),
    [openUserOverview, sort],
  );

  const handleApplyFilters = ({ dates: nextDates, filters }) => {
    setDates(nextDates);
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const nextSort = toSortValue(sorter);
    setSort((previousSort) => {
      if (previousSort !== nextSort) setPage(1);
      return nextSort;
    });
  };

  const buildExportPayload = useCallback(
    () =>
      buildPaymentTransactionsExportPayload({
        dates,
        search,
        filters: appliedFilters,
      }),
    [appliedFilters, dates, search],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.tableContainer}>
            <div className={styles.toolbar}>
              <div className={styles.searchGroup}>
                <Input
                  className={styles.searchInput}
                  variant="search"
                  type="search"
                  allowClear
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder={AppStrings.PAM_REPORT_SEARCH_PLACEHOLDER}
                  aria-label={AppStrings.PAM_REPORT_SEARCH_PLACEHOLDER}
                />
              </div>

              <button
                type="button"
                className={styles.toolbarButton}
                onClick={() => setFilterDrawerOpen(true)}
              >
                <CalendarOutlined aria-hidden />
                <span>{getDateRangeLabel(dates)}</span>
              </button>

              <button
                type="button"
                className={styles.toolbarButton}
                onClick={() => setFilterDrawerOpen(true)}
              >
                <FilterOutlined aria-hidden />
                <span>{AppStrings.PAM_REPORT_FILTERS}</span>
              </button>

              <div className={styles.toolbarActions}>
                <button
                  type="button"
                  className={styles.toolbarButton}
                  onClick={() => setSettingsOpen(true)}
                >
                  <span>{AppStrings.PAM_REPORT_TABLE}</span>
                  <ControlOutlined aria-hidden />
                </button>

                {canExport && (
                  <AsyncExportButton
                    columns={exportColumns}
                    buildPayload={buildExportPayload}
                    exportState={exportState}
                    disabled={
                      exportColumnsQuery.isLoading ||
                      exportColumnsQuery.isError ||
                      exportColumns.length === 0
                    }
                    disabledTooltip={
                      exportColumnsQuery.isError
                        ? AppStrings.PAYMENTS_TRANSACTIONS_EXPORT_START_ERROR
                        : undefined
                    }
                  />
                )}
              </div>
            </div>

            <div className={styles.table}>
              <ConfigurableDataTable
                id="report-generator-v2"
                className={styles.reportTable}
                rowKey="id"
                columns={columns}
                dataSource={transactionsQuery.items}
                loading={
                  transactionsQuery.isLoading ||
                  transactionsQuery.isFetching
                }
                onChange={handleTableChange}
                settingsOpen={settingsOpen}
                onSettingsClose={() => setSettingsOpen(false)}
                onRow={(record) => ({
                  className: styles.rowClickable,
                  style: { cursor: "pointer" },
                  onClick: () =>
                    setPaymentDetails({
                      userId: record.player,
                      transactionId: record.id,
                    }),
                })}
                paginationConfig={{
                  currentPage: page,
                  pageSize: size,
                  total: transactionsQuery.total,
                  pageSizeOptions: [10, 20, 50, 100],
                  summaryPrefix: AppStrings.PAM_PAGINATION_RETURNING,
                  summaryConnector: AppStrings.PAM_PAGINATION_OF,
                  onPageChange: setPage,
                  onPageSizeChange: (nextSize) => {
                    setSize(nextSize);
                    setPage(1);
                  },
                }}
                scroll={{ x: 2400, y: 400 }}
                fillHeight
              />
            </div>
          </div>
        </div>
      </div>

      <ReportFiltersDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        dates={dates}
        appliedFilters={appliedFilters}
        filterMeta={transactionsQuery.filters}
        onApply={handleApplyFilters}
      />

      <PaymentDetailsDrawer
        open={Boolean(paymentDetails)}
        onClose={() => setPaymentDetails(null)}
        userId={paymentDetails?.userId}
        transactionId={paymentDetails?.transactionId}
        detailsRoute={API_ROUTES.GET_USER_TRANSACTION_DETAILS_V2}
      />
    </>
  );
}
